/* eslint-disable @typescript-eslint/no-explicit-any */
import { AnyObject } from 'types';
import { storage as appStorage, AppStorageType } from './storage';

interface StorageConfig<DataType> {
  key: string;

  /**
   * Defaults to durable
   */
  storage?: AppStorageType;

  validate?: (value: DataType) => boolean;

  defaultValue?: DataType;

  getValidity?: (value: DataType) => Date;

  serialize?: (value: DataType) => string;
  revive?: (value: any) => DataType;
}

type ConfigFromMapping<T extends AnyObject> = {
  [Key in keyof T]: string | StorageConfig<T[Key]>;
};

type SingleEntityStorage<Data> = {
  get(): Data | undefined;

  set(data: Data): void;

  delete(): void;
};

type TypedStorage<T> = {
  [Key in keyof T]: SingleEntityStorage<T[Key]>;
};

function buildEntityStorage(config: StorageConfig<any> | string): SingleEntityStorage<any> {
  const {
    key,
    storage = 'durable',
    validate = () => true,
    defaultValue,
    getValidity,
    revive = (s) => s,
    serialize,
  } = typeof config === 'string'
    ? ({ key: config, storage: 'durable' } as StorageConfig<any>)
    : config;

  const target = appStorage[storage];

  return {
    delete: () => target.delete(key),

    get: () => {
      const data = target.get(key);

      if (!data) return defaultValue;

      const revived = revive(data);

      if (validate(revived)) return revived;
    },

    set: (data) => target.set(key, serialize?.(data) ?? data, getValidity?.(data)),
  };
}

/**
 *
 * Initiate a TYPED web storage instance
 *
 * Pass in an object type that has:
 * - keys: the data names the storage will be aware of
 * - values: the type of that data
 *
 * With this definition, you are free to init the storage and have
 * a typed way of retrieving the data from whatever configuration you have decided
 */
export function typedStorage<DataMapping extends AnyObject>(): {
  init: (config: ConfigFromMapping<DataMapping>) => TypedStorage<DataMapping>;
} {
  return {
    init: (config) =>
      Object.fromEntries(
        Object.entries(config).map(([key, config]) => [key, buildEntityStorage(config)]),
      ) as TypedStorage<DataMapping>,
  };
}
